<template>
  <div class="expandableGrid position-relative">

    <div class="component-row">
      <div class="component-col flex-grow-1">
        <table v-if="!editingData" class="table" :class="classes" v-html="difference" />

        <div v-else>
          <table class="table">
            <thead>
              <tr><th v-for="(header, index) in headings" :key="index">{{ header }}</th></tr>
            </thead>
            <tbody v-for="(item, row) in form.value" :key="row">
              <tr>
                <td v-for="(value, index) in item" :key="index">
                  <Textarea v-model="form.value[row][index]" />

                  <small v-if="row === form.value.length - 1" class="text-muted">{{ helpers[index] }}</small>
                </td>
              </tr>
              <tr>
                <td :colspan="form.value[row].length" class="border-top-0 pt-0">
                  <button v-if="!deleting[row]" @click.prevent="showDelete(row, true)" class="btn btn-danger " :class="{ active: versions }">Delete</button>
                  <div v-else>
                    Are you sure?
                    <button @click.prevent="deleteItem(row)" class="btn btn-danger">Yes</button>
                    <button @click.prevent="showDelete(row, false)" class="btn btn-secondary">No</button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="flex-grow-1 mt-2 mb-4">
            <LoadingButton @click.native="addItem" class="btn btn-primary" :loading="adding">{{ block.settings.label }}</LoadingButton>
          </div>

        </div>
      </div>

      <div v-if="showActions && !editingData" class="component-col d-flex align-items-start flex-grow-0 ">
        <div class="d-flex flex-nowrap">
          <Tag @click.native="editingData = true" :block="blockData" :withCounterparty="state.withCounterparty" />
          <CommentsTrigger v-on:toggle="comments = !comments" :showing="comments" :comments="commentsData" />
        </div>
      </div>
    </div>

    <Actions
      v-if="showActions && editingData"

      @updated="updated"
      @save="save"
      @cancel="cancel"

      :saving="saving"
      :block="blockData"

      :asset-id="asset.id"
      :model="asset.model"

      :changed="changed"
    />

    <Comments
      v-if="comments"
      :comments="commentsData"
      :contract="block.contract_id"
      :block="block.id"
      v-on:commentsUpdated="commentsUpdated"
      v-on:toggle="comments = !comments"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import contractHelpers from '@/Mixins/contractHelpers';
import helpers from '@/Mixins/helpers';
import Actions from '@/Pages/Asset/Contract/Actions/Actions';
import Textarea from '@/Shared/Inputs/Textarea';
import LoadingButton from '@/Shared/LoadingButton';

import Comments from '@/Pages/Asset/Contract/Comments/Comments';
import CommentsTrigger from '@/Pages/Asset/Contract/Comments/CommentsTrigger';
import Tag from '@/Pages/Asset/Contract/Actions/Tag';

export default {
  mixins: [contractHelpers, helpers],

  components: {
    Actions,
    Textarea,
    Comments,
    CommentsTrigger,
    Tag,
    LoadingButton,
  },

  props: {
    dynamic: {
      type: Boolean,
      default: false,
    },
    block: Object,
    blocks: Array,
    definitions: Array,
    showActions: Boolean,
    asset: Object,
    deletable: Boolean,
    editing: {
      type: Boolean,
      default: false,
    },
    saveTrigger: String,
    blockReference: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      adding: false,
      deleting: this.block.value ? this.block.value.map(() => false) : [],

      blockData: this.block,
      commentsData: this.block.comments,

      editingData: this.editing,
      versions: false,
      comments: false,
      saving: false,
      errors: null,

      form: {
        value: this.block.value,
      },

      // For some unknown reason, when we update this.form.value,
      // this.block.value is updateing too.
      // So here we are storing the orginal this.block.value as a string
      // so we can decode it if we need to reveser the changes (cancel())
      holdValue: JSON.stringify(this.block.value),
    };
  },

  watch: {
    block(block) {
      this.form.value = block.value;
    },
    editing(value) {
      this.editingData = value;
    },
    saveTrigger() {
      this.save();
    },
  },

  computed: {
    helpers() {
      return this.blockData.template.helpers;
    },

    headings() {
      return this.blockData.template.headings;
    },

    headersHtml() {
      if (!this.headings) return null;

      let html = '<thead><tr>';

      this.headings.forEach((header) => {
        html = `${html}<th>${header}</th>`;
      });

      html = `${html}</tr></thead>`;

      return html;
    },

    rows() {
      if (!this.form.value) return null;

      const rows = [];

      this.form.value.forEach((row) => {
        let html = '';
        row.forEach((value) => {
          html = `${html}<td>${value}</td>`;
        });
        rows.push(html);
      });

      return rows;
    },

    latestRows() {
      if (!this.blockData.latest_version.value) return null;

      const rows = [];

      this.blockData.latest_version.value.forEach((row) => {
        let html = '';
        row.forEach((value) => {
          html = `${html}<td>${value}</td>`;
        });
        rows.push(html);
      });

      return rows;
    },

    rowsHtml() {
      if (!this.form.value) return null;

      let html = '';

      this.rows.forEach((row) => {
        html = `${html}<tr>${row}</tr>`;
      });

      return html;
    },

    difference() {
      let text = this.rowsHtml || `<tr><td colspan="${this.headings.length + 1}">Add Items</td></tr>`;
      if (this.blockData.latest_version) text = this.compareTable(this.rows, this.latestRows);
      let colour = '';

      switch (this.blockData.state) {
        case 'incomplete':
          colour = 'text-danger';
          break;

        case 'pending':
          colour = this.userHasRole('approver') ? 'text-danger' : 'text-success';
          break;

        case 'originatorApproved':
          colour = this.state.withCounterparty ? 'text-danger' : 'text-success';
          break;

        case 'counterpartyApproved':
          colour = !this.state.withCounterparty ? 'text-danger' : 'text-success';
          break;

        case 'accepted':
          colour = this.asset.state === 'executed' ? 'text-body' : 'text-success';
          text = this.rowsHtml;
          break;

        default:
          colour = 'text-danger';
          break;
      }

      return `${this.headersHtml}<tbody class="${colour}">${text}</tbody>`;
    },

    changed() {
      return this.holdValue !== JSON.stringify(this.form.value);
    },
  },

  methods: {
    showDelete(index, value) {
      Vue.set(this.deleting, index, value);
    },

    addItem() {
      const row = [];

      this.headings.forEach(() => {
        row.push('');
      });

      this.form.value.push(row);
    },

    deleteItem(index) {
      this.form.value.splice(index, 1);
      this.deleting = this.block.value.map(() => false);
    },

    updated(block) {
      this.blockData = block;
    },

    save() {
      this.saving = true;

      this.$nextTick(() => {
        window.axios
          .put(`/contract-block/${this.block.id}`, this.form)
          .then((response) => {

            this.saving = false;
            this.editingData = this.userHasRole('approver');
            this.blockData = response.data;
            this.fetchAssetState(this.asset.id);

            // Replace the holdValue if you decide to cancel again
            this.holdValue = JSON.stringify(this.form.value);
          })
          .catch((error) => {
            this.saving = false;
            // this.errors = error.response.data.errors;
            throw new Error(error);
          });
      });
    },

    cancel() {
      this.form.value = JSON.parse(this.holdValue);
      this.editingData = false;
    },

    commentsUpdated(comments) {
      this.commentsData = comments;
    },
  },
};
</script>
